/**
 * Tenant Form Validation
 *
 * Pure validation functions for tenant form data.
 * No external dependencies - only uses types.
 *
 * @module features/tenants/utils/validation
 */

import type { TenantFormData, FormErrors } from '../types';

// ============================================================================
// Field Validators
// ============================================================================

/**
 * Validate a slug format.
 * Rules:
 * - Must start with a letter
 * - Can contain lowercase letters, numbers, and hyphens
 * - For slugs > 2 chars: must match /^[a-z][a-z0-9-]*[a-z0-9]$/
 * - For slugs <= 2 chars: must match /^[a-z][a-z0-9]?$/
 */
export const isValidSlug = (slug: string): boolean => {
  if (!slug) return false;
  if (slug.length > 2) {
    return /^[a-z][a-z0-9-]*[a-z0-9]$/.test(slug);
  }
  return /^[a-z][a-z0-9]?$/.test(slug);
};

/**
 * Validate an email address format.
 */
export const isValidEmail = (email: string): boolean => {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/**
 * Validate a hex color format (#RRGGBB).
 */
export const isValidHexColor = (color: string): boolean => {
  if (!color) return true; // Empty is valid (optional field)
  return /^#[0-9A-Fa-f]{6}$/.test(color);
};

/**
 * Validate a URL format (optional - can be empty).
 */
export const isValidUrl = (url: string): boolean => {
  if (!url) return true; // Empty is valid (optional field)
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// ============================================================================
// Form Validation
// ============================================================================

/**
 * Validate the complete tenant form.
 *
 * @param data - Form data to validate
 * @param isEditing - Whether editing existing tenant (slug readonly)
 * @returns Object with field names as keys and error messages as values
 */
export const validateTenantForm = (
  data: TenantFormData,
  isEditing: boolean = false
): FormErrors => {
  const errors: FormErrors = {};

  // Slug validation (required for new tenants, skip for edit since readonly)
  if (!isEditing) {
    if (!data.slug) {
      errors.slug = 'URL Identifier is required';
    } else if (data.slug.length > 2 && !isValidSlug(data.slug)) {
      errors.slug = 'Must start with letter, contain only lowercase letters, numbers, hyphens';
    } else if (data.slug.length <= 2 && !/^[a-z][a-z0-9]?$/.test(data.slug)) {
      errors.slug = 'Short identifiers must be lowercase letters/numbers only';
    }
  }

  // Name validation
  if (!data.name || data.name.length < 2) {
    errors.name = 'Name must be at least 2 characters';
  }

  // Admin email validation
  if (!data.admin_email) {
    errors.admin_email = 'Admin email is required';
  } else if (!isValidEmail(data.admin_email)) {
    errors.admin_email = 'Invalid email format';
  }

  // Branding color validations (optional but must be valid format if provided)
  if (data.branding.primaryColor && !isValidHexColor(data.branding.primaryColor)) {
    errors.primaryColor = 'Invalid hex color format';
  }

  if (data.branding.secondaryColor && !isValidHexColor(data.branding.secondaryColor)) {
    errors.secondaryColor = 'Invalid hex color format';
  }

  return errors;
};

/**
 * Check if form has any validation errors.
 */
export const hasErrors = (errors: FormErrors): boolean => {
  return Object.keys(errors).length > 0;
};
